import { Directive } from "@angular/core";
import { Deck, DeckCard, DeckLayer } from "@apiserver-rest/ponder-data-client-base";
import { DeckGroups } from "./group/deck-groups";
import { DeckBaseComponent } from "./deck-base.component";
import { getScreenBreakpoint, ScreenBreakpoint } from "@ponder-shared/utils/screenBreakpoints";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { Subscription } from "rxjs";
import { DeckLayerName } from "@apiserver-rest/ponder-data-client";
import { DeckGroupComponent } from "./group/deck-group.component";
import { DeckGroup } from "./group/deck-group";
import { DeckCardEvent } from "@ponder-core/events/deck.events";

@Directive()
export class DeckLayerComponent {
    public isOwnDeck!: boolean;
    public name!: DeckLayerName;
    public deck!: Deck;
    public deckLayer!: DeckLayer;
    public deckComponent!: DeckBaseComponent;
    public isVisible: boolean = false;
    public groups!: DeckGroups;
    public columnCount: number = 1;

    private groupComponents: DeckGroupComponent[] = [];
    private resizeEventSub!: Subscription;
    private deckCardSub!: Subscription;

    public constructor(protected events: EventBroker) {

    }

    public ngOnInit(): void {
        this.updateColumnCount();

        this.resizeEventSub = this.events.ui.windowResizeEvent.subscribe({
            next: () => {
                this.onScreenResize();
            }
        });

        this.deckCardSub = this.events.deck.deckCardEvent.subscribe({
            next: (event: DeckCardEvent) => {
                this.onDeckCardEvent(event);
            }
        });
    }

    public ngOnDestroy() {
        this.resizeEventSub?.unsubscribe();
        this.deckCardSub?.unsubscribe();
    }
    
    // -------------------------------------------------------------------------------------
    // Deck Card Change Event
    // -------------------------------------------------------------------------------------

    private onDeckCardEvent(event: DeckCardEvent): void {
        if (event.layer != this.name) {
            return;
        }
        if ((event.prevCount == 0) && (event.prevSpecialCount == 0) && ((event.currCount > 0) || (event.currSpecialCount > 0))) {
            this.addCard(event.deckCard);
        } else if (event.currCount == 0 && event.currSpecialCount == 0) {
            this.removeCard(event.deckCard);
        } else {
            this.updateCard(event.deckCard);
        }

        this.recalculateTotals();
    }

    // -------------------------------------------------------------------------------------
    // Group Actions
    // -------------------------------------------------------------------------------------

    public registerGroupComponent(groupComponent: DeckGroupComponent): void {
        this.groupComponents.push(groupComponent);
    }

    public getGroupComponent(deckGroup: DeckGroup | undefined): DeckGroupComponent | undefined {
        if (!deckGroup) {
            return undefined;
        }

        for (let i = 0; i < this.groupComponents.length; ++i) {
            if (this.groupComponents[i].group.name === deckGroup.name) {
                return this.groupComponents[i];
            }
        }

        return undefined;
    }

    // -------------------------------------------------------------------------------------
    // Card Actions
    // -------------------------------------------------------------------------------------

    public getCard(cardName: string | undefined, cardId: string | undefined): DeckCard | undefined {
        if (!this.deckLayer.cards) {
            return undefined;
        }

        if (cardName) {
            return this.deckLayer.cards.find((dc: DeckCard, _, __) => dc.card.item.name === cardName);
        } else if (cardId) {
            return this.deckLayer.cards.find((dc: DeckCard, _, __) => dc.card.item.id === cardId);
        }

        return undefined;
    }

    public addCard(card: DeckCard): DecklayerAddCardResult {
        var existingDeckCard = this.getCard(undefined, card.card.item.id);

        if (existingDeckCard) {
            existingDeckCard.count += card.count;
            existingDeckCard.specialCount += card.specialCount;

            return { 
                action: DeckLayerAddCardActionType.Updated, 
                deckCard: existingDeckCard 
            };
        } else {
            // Add to logical container
            this.deckLayer.cards = this.deckLayer.cards ?? [];
            this.deckLayer.cards.push(card);

            // Add to logical group
            var group = this.groups.addCard(card);
    
            if (group) {
                // Add to visible group
                this.getGroupComponent(group)?.addCard(card);
            }

            return {
                action: DeckLayerAddCardActionType.Added, 
                deckCard: card
            };
        }
    }

    public removeCard(card: DeckCard): void {
        // Remove from visible group
        var prevGroup = this.groups.removeCard(card);

        if (prevGroup) {
            this.getGroupComponent(prevGroup)?.removeCard(card);
        }

        // Remove from logical container
        var index = -1;

        for (let i = 0; i < this.deckLayer.cards!.length; ++i) {
            if (this.deckLayer.cards![i] === card) {
                index = i;
            }
        }

        if (index >= 0) {
            this.deckLayer.cards!.splice(index, 1);
        }
    }

    public updateCard(card: DeckCard): void {
        var group = this.groups.getGroup(card);

        if (group) {
            group.recalculate();
        }
    }

    public recalculateTotals(): void {
        this.deckLayer.distinctCount = 0;
        this.deckLayer.totalCount = 0;

        this.deckLayer.cards?.forEach(c => {
            this.deckLayer.distinctCount++;
            this.deckLayer.totalCount += c.count + c.specialCount;
        })
    }
    
    // -------------------------------------------------------------------------------------
    // Misc
    // -------------------------------------------------------------------------------------

    protected buildGroupings(groups: string[]): void {

    }

    protected onScreenResize(): void {
        this.updateColumnCount();
    }

    private updateColumnCount(): void {
        switch (getScreenBreakpoint()) {
            // This column count needs to match the sizing done in magic-deck.component.html
            case ScreenBreakpoint.Mobile:
            case ScreenBreakpoint.Small:
                this.columnCount = 1;
                break;

            case ScreenBreakpoint.Medium:
            case ScreenBreakpoint.Large:
            case ScreenBreakpoint.XLarge:
                this.columnCount = 2;
                break;

            case ScreenBreakpoint.XXLarge:
                this.columnCount = 3;
                break;
        }
    }
}

export enum DeckLayerAddCardActionType {
    None = 0,
    Updated = 1,
    Added = 2
};

export type DecklayerAddCardResult = {
    action: DeckLayerAddCardActionType;
    deckCard: DeckCard;
}